import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  BanknotesIcon,
  ClipboardDocumentListIcon,
  CubeIcon,
  DocumentTextIcon,
  FolderIcon,
  MapPinIcon,
  ScaleIcon,
  ShoppingCartIcon,
  Squares2X2Icon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import { AppShell } from "@/components/AppShell";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Spinner } from "@/components/ui/Spinner";
import { Dialog } from "@/components/ui/Dialog";
import { hasFormChanges } from "@/components/ui/dialogCloseGuard";
import { TextareaInput, TextInput } from "@/components/ui/Field";
import { PartnerNameFields } from "@/pages/kontakte/PartnerNameFields";
import {
  partnerNameFromRecord,
  partnerNamePayload,
  validatePartnerName,
  type PartnerNameForm,
} from "@/pages/kontakte/partnerart";
import { ApiError } from "@/lib/apiClient";
import {
  deleteGeschaeftspartner,
  getGeschaeftspartner,
  updateGeschaeftspartner,
  type Geschaeftspartner,
  type GeschaeftspartnerIn,
  type GeschaeftspartnerTyp,
} from "@/lib/kontakte";
import { forgetGeschaeftspartnerList } from "@/pages/kontakte/geschaeftspartnerListCache";
import { useRealtimeSync } from "@/lib/useRealtime";
import { AnsprechpartnerSection } from "@/pages/kontakte/AnsprechpartnerSection";
import { KundeBestellnummerRegelnSection } from "@/pages/kontakte/KundeBestellnummerRegelnSection";
import { KundeRechnungsadressenSection } from "@/pages/kontakte/KundeRechnungsadressenSection";
import { KundeRechnungsEinstellungenSection } from "@/pages/kontakte/KundeRechnungsEinstellungenSection";
import { KundeMahnwesenSection } from "@/pages/kontakte/KundeMahnwesenSection";
import { KundeProjekteSection } from "@/pages/kontakte/KundeProjekteSection";
import { listOrdersWithTotal } from "@/lib/projects";
import { KundeZahlungsbedingungenSection } from "@/pages/kontakte/KundeZahlungsbedingungenSection";
import { ObjektadressenSection } from "@/pages/kontakte/ObjektadressenSection";
import { LieferantEingangsrechnungSection } from "@/pages/kontakte/LieferantEingangsrechnungSection";
import { DetailTabs, type DetailTabOption } from "@/pages/kontakte/detail/DetailTabs";
import { GeschaeftspartnerHeader } from "@/pages/kontakte/detail/GeschaeftspartnerHeader";
import { GeschaeftspartnerUebersicht } from "@/pages/kontakte/detail/GeschaeftspartnerUebersicht";
import {
  isDetailTab,
  type DetailTab,
} from "@/pages/kontakte/detail/geschaeftspartnerDetailUtils";

interface GpForm extends PartnerNameForm {
  email: string;
  telefon: string;
  adresszeile1: string;
  adresszeile2: string;
  strasse: string;
  plz: string;
  ort: string;
  kundencode: string;
  kundennummer: string;
  lieferantennummer: string;
  notizen: string;
}

const EMPTY_FORM: GpForm = {
  ...partnerNameFromRecord({}),
  email: "",
  telefon: "",
  adresszeile1: "",
  adresszeile2: "",
  strasse: "",
  plz: "",
  ort: "",
  kundencode: "",
  kundennummer: "",
  lieferantennummer: "",
  notizen: "",
};

function toForm(gp: Geschaeftspartner): GpForm {
  return {
    ...partnerNameFromRecord(gp),
    email: gp.email || "",
    telefon: gp.telefon || "",
    adresszeile1: gp.adresszeile1 || "",
    adresszeile2: gp.adresszeile2 || "",
    strasse: gp.strasse || "",
    plz: gp.plz || "",
    ort: gp.ort || "",
    kundencode: gp.kundencode != null ? String(gp.kundencode) : "",
    kundennummer: gp.kundennummer || "",
    lieferantennummer: gp.lieferantennummer || "",
    notizen: gp.notizen || "",
  };
}

const TAB_PARAM = "tab";

interface GeschaeftspartnerDetailPageProps {
  typ: GeschaeftspartnerTyp;
  listPath: string;
  listLabel: string;
}

export function GeschaeftspartnerDetailPage({
  typ,
  listPath,
  listLabel,
}: GeschaeftspartnerDetailPageProps) {
  const navigate = useNavigate();
  const { id: idParam } = useParams<{ id: string }>();
  const gpId = Number(idParam);
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightedAnsprechpartnerIds = useMemo(() => {
    return (searchParams.get("ansprechpartner") || "")
      .split(",")
      .map((part) => Number(part.trim()))
      .filter((id) => Number.isFinite(id) && id > 0);
  }, [searchParams]);

  const [gp, setGp] = useState<Geschaeftspartner | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [form, setForm] = useState<GpForm>(EMPTY_FORM);
  const [formBaseline, setFormBaseline] = useState<GpForm>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [projekteCount, setProjekteCount] = useState<number | null>(null);

  const tabOptions = useMemo<DetailTabOption<DetailTab>[]>(() => {
    const apCount = gp?.ansprechpartner_count ?? null;
    if (typ === "lieferant") {
      return [
        { value: "uebersicht", label: "Übersicht", icon: Squares2X2Icon },
        { value: "kontakte", label: "Ansprechpartner", icon: UsersIcon, count: apCount },
        { value: "adressen", label: "Objektadressen", icon: MapPinIcon },
        { value: "einkauf", label: "Einkauf", icon: ShoppingCartIcon, count: gp?.artikel_count ?? null },
      ];
    }
    return [
      { value: "uebersicht", label: "Übersicht", icon: Squares2X2Icon },
      { value: "kontakte", label: "Ansprechpartner", icon: UsersIcon, count: apCount },
      { value: "adressen", label: "Objektadressen", icon: MapPinIcon },
      { value: "bestellung", label: "Bestellnummern", icon: ClipboardDocumentListIcon },
      { value: "zahlung", label: "Zahlung", icon: BanknotesIcon },
      { value: "rechnung", label: "Rechnung", icon: DocumentTextIcon },
      { value: "forderungen", label: "Forderungen", icon: ScaleIcon },
      { value: "projekte", label: "Projekte", icon: FolderIcon, count: projekteCount },
    ];
  }, [typ, gp?.ansprechpartner_count, gp?.artikel_count, projekteCount]);

  // Aktiver Tab lebt in der URL (?tab=…), damit Links teilbar sind und der
  // Zurück-Button innerhalb der Seite sinnvoll funktioniert.
  const requestedTab = searchParams.get(TAB_PARAM);
  const activeTab: DetailTab = useMemo(() => {
    if (isDetailTab(requestedTab) && tabOptions.some((opt) => opt.value === requestedTab)) {
      return requestedTab;
    }
    return "uebersicht";
  }, [requestedTab, tabOptions]);

  const setActiveTab = useCallback(
    (next: DetailTab) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next === "uebersicht") params.delete(TAB_PARAM);
          else params.set(TAB_PARAM, next);
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const load = useCallback(async (options?: { silent?: boolean }) => {
    if (!Number.isFinite(gpId)) return;
    if (!options?.silent) setLoading(true);
    setError(null);
    try {
      const row = await getGeschaeftspartner(gpId);
      if (row.typ !== typ) {
        setError("Eintrag gehört nicht zu diesem Bereich.");
        setGp(null);
        return;
      }
      setGp(row);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Eintrag konnte nicht geladen werden.",
      );
      setGp(null);
    } finally {
      setLoading(false);
    }
  }, [gpId, typ]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (typ !== "kunde" || !Number.isFinite(gpId)) return;
    let cancelled = false;
    setProjekteCount(null);
    void listOrdersWithTotal({
      geschaeftspartner_id: gpId,
      limit: 1,
      include_timesheets: false,
      include_timesheet_numbers: false,
      include_tags: false,
      include_hero_custom_fields: false,
      include_hero_steps: false,
    })
      .then((result) => {
        if (!cancelled && result.totalCount != null) setProjekteCount(result.totalCount);
      })
      .catch(() => {
        /* Die Badge bleibt leer, bis der Reiter die Liste selbst lädt. */
      });
    return () => {
      cancelled = true;
    };
  }, [typ, gpId]);

  useRealtimeSync({
    domains: ["kontakte"],
    paused: editOpen || deleteOpen,
    enabled: Number.isFinite(gpId),
    onChange: () => void load({ silent: true }),
  });

  function openDelete() {
    setDeleteError(null);
    setDeleteOpen(true);
  }

  async function confirmDelete() {
    if (!gp || gp.kann_geloescht_werden === false) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteGeschaeftspartner(gp.id);
      forgetGeschaeftspartnerList(typ);
      navigate(listPath);
    } catch (err) {
      setDeleteError(err instanceof ApiError ? err.message : "Löschen fehlgeschlagen.");
    } finally {
      setDeleting(false);
    }
  }

  function openEdit() {
    if (!gp) return;
    const initial = toForm(gp);
    setForm(initial);
    setFormBaseline(initial);
    setFormError(null);
    setEditOpen(true);
  }

  async function handleSave() {
    if (!gp) return;
    const nameError = validatePartnerName(form, typ);
    if (nameError) {
      setFormError(nameError);
      return;
    }
    let kundencode: number | null = null;
    if (typ === "kunde") {
      const rawCode = form.kundencode.trim();
      if (rawCode) {
        const parsed = Number.parseInt(rawCode, 10);
        if (!Number.isFinite(parsed) || parsed < 1) {
          setFormError("Bitte einen gültigen Kundencode eingeben (positive Zahl).");
          return;
        }
        kundencode = parsed;
      }
    }
    const payload: GeschaeftspartnerIn = {
      ...partnerNamePayload(form, typ),
      email: form.email.trim() || null,
      telefon: form.telefon.trim() || null,
      adresszeile1: form.adresszeile1.trim() || null,
      adresszeile2: form.adresszeile2.trim() || null,
      strasse: form.strasse.trim() || null,
      plz: form.plz.trim() || null,
      ort: form.ort.trim() || null,
      ...(typ === "kunde"
        ? {
            kundencode,
            kundennummer: form.kundennummer.trim() || null,
          }
        : {
            lieferantennummer: form.lieferantennummer.trim() || null,
          }),
      notizen: form.notizen.trim() || null,
    };
    setSaving(true);
    setFormError(null);
    try {
      const updated = await updateGeschaeftspartner(gp.id, payload);
      setGp(updated);
      setEditOpen(false);
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  if (!Number.isFinite(gpId)) {
    return (
      <AppShell title={listLabel} back={{ to: listPath, label: listLabel }}>
        <Alert variant="error" title="Ungültiger Link">
          Keine gültige ID angegeben.
        </Alert>
      </AppShell>
    );
  }

  if (loading) {
    return (
      <AppShell title={listLabel} back={{ to: listPath, label: listLabel }}>
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      </AppShell>
    );
  }

  if (!gp) {
    return (
      <AppShell title={listLabel} back={{ to: listPath, label: listLabel }}>
        <Alert variant="error" title="Nicht gefunden">
          {error || "Eintrag nicht gefunden."}
        </Alert>
      </AppShell>
    );
  }

  return (
    <AppShell
      title={gp.firmenname}
      subtitle={listLabel}
      back={{ to: listPath, label: listLabel }}
    >
      {error ? (
        <Alert variant="error" title="Hinweis" className="mb-4">
          {error}
        </Alert>
      ) : null}

      <div className="space-y-5">
        <GeschaeftspartnerHeader
          gp={gp}
          typ={typ}
          onEdit={openEdit}
          onDelete={openDelete}
          onNavigate={setActiveTab}
          onLogoChange={(updated) =>
            setGp((current) =>
              current
                ? {
                    ...current,
                    has_logo: updated.has_logo,
                    logo_updated_at: updated.logo_updated_at,
                  }
                : updated,
            )
          }
        />

        <DetailTabs
          value={activeTab}
          onChange={setActiveTab}
          options={tabOptions}
          ariaLabel="Bereiche"
        />

        <div className="space-y-4">
          {activeTab === "uebersicht" ? (
            <GeschaeftspartnerUebersicht
              gp={gp}
              typ={typ}
              onEdit={openEdit}
              onNavigate={setActiveTab}
            />
          ) : null}

          {activeTab === "kontakte" ? (
            <AnsprechpartnerSection
              geschaeftspartnerId={gp.id}
              highlightIds={highlightedAnsprechpartnerIds}
            />
          ) : null}

          {activeTab === "adressen" ? (
            <ObjektadressenSection
              heroContactId={gp.hero_contact_id}
              geschaeftspartnerId={gp.id}
            />
          ) : null}

          {activeTab === "einkauf" && typ === "lieferant" ? (
            <>
              <LieferantEingangsrechnungSection geschaeftspartnerId={gp.id} />
              <Card title="Stamm-Artikel">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-fill-tertiary text-label-secondary">
                    <CubeIcon className="h-5 w-5" aria-hidden />
                  </span>
                  <div>
                    <div className="text-lg font-semibold tabular-nums text-label">
                      {(gp.artikel_count ?? 0).toLocaleString("de-DE")}
                    </div>
                    <p className="text-sm text-label-secondary">
                      {gp.artikel_count != null && gp.artikel_count > 0
                        ? "Artikel dieses Lieferanten sind im Artikelstamm hinterlegt."
                        : "Noch keine Artikel dieses Lieferanten im Artikelstamm."}
                    </p>
                  </div>
                </div>
              </Card>
            </>
          ) : null}

          {activeTab === "bestellung" && typ === "kunde" ? (
            <KundeBestellnummerRegelnSection
              geschaeftspartnerId={gp.id}
              heroContactId={gp.hero_contact_id}
            />
          ) : null}

          {activeTab === "zahlung" && typ === "kunde" ? (
            <KundeZahlungsbedingungenSection
              geschaeftspartnerId={gp.id}
              override={gp.zahlungsbedingungen}
              effektiv={gp.zahlungsbedingungen_effektiv}
              onUpdated={({ override, effektiv: nextEffektiv }) =>
                setGp((current) =>
                  current
                    ? {
                        ...current,
                        zahlungsbedingungen: override,
                        zahlungsbedingungen_effektiv: nextEffektiv,
                      }
                    : current,
                )
              }
            />
          ) : null}

          {activeTab === "rechnung" && typ === "kunde" ? (
            <>
              <KundeRechnungsadressenSection geschaeftspartnerId={gp.id} />
              <KundeRechnungsEinstellungenSection
                geschaeftspartnerId={gp.id}
                einstellungen={gp.rechnungs_einstellungen}
                defaultEmail={gp.email}
                onUpdated={(einstellungen) =>
                  setGp((current) =>
                    current ? { ...current, rechnungs_einstellungen: einstellungen } : current,
                  )
                }
              />
            </>
          ) : null}

          {activeTab === "forderungen" && typ === "kunde" ? (
            <KundeMahnwesenSection geschaeftspartnerId={gp.id} />
          ) : null}

          {activeTab === "projekte" && typ === "kunde" ? (
            <KundeProjekteSection
              geschaeftspartnerId={gp.id}
              onTotalCount={setProjekteCount}
            />
          ) : null}
        </div>
      </div>

      <Dialog
        open={deleteOpen}
        onClose={() => {
          if (!deleting) setDeleteOpen(false);
        }}
        title={typ === "kunde" ? "Kunde löschen" : "Lieferant löschen"}
        footer={
          gp.kann_geloescht_werden === false ? (
            <Button variant="secondary" onClick={() => setDeleteOpen(false)}>
              Schließen
            </Button>
          ) : (
            <>
              <Button variant="secondary" onClick={() => setDeleteOpen(false)} disabled={deleting}>
                Abbrechen
              </Button>
              <Button variant="danger" onClick={() => void confirmDelete()} loading={deleting}>
                Löschen
              </Button>
            </>
          )
        }
      >
        {deleteError ? (
          <Alert variant="error" title="Löschen" className="mb-4">
            {deleteError}
          </Alert>
        ) : null}
        {gp.kann_geloescht_werden === false ? (
          <p className="text-sm text-label">
            {gp.loesch_hinweis ||
              (typ === "kunde"
                ? "Dieser Kunde hat bereits Projekte und kann nicht gelöscht werden."
                : "Dieser Lieferant hat bereits Bestellungen und kann nicht gelöscht werden.")}
          </p>
        ) : (
          <p className="text-sm text-label">
            „{gp.firmenname}“ wirklich löschen? Der Eintrag verschwindet aus der Liste. Das geht
            nur, solange noch keine {typ === "kunde" ? "Projekte" : "Bestellungen"} daran hängen.
          </p>
        )}
      </Dialog>

      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title="Stammdaten bearbeiten"
        dirty={hasFormChanges(form, formBaseline)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditOpen(false)} disabled={saving}>
              Abbrechen
            </Button>
            <Button onClick={() => void handleSave()} disabled={saving}>
              {saving ? "Speichern…" : "Speichern"}
            </Button>
          </>
        }
      >
        {formError ? (
          <Alert variant="error" title="Eingabe" className="mb-4">
            {formError}
          </Alert>
        ) : null}
        <div className="grid gap-4 sm:grid-cols-2">
          {typ === "kunde" ? (
            <>
              <TextInput
                label="Kundennummer"
                value={form.kundennummer}
                onChange={(e) => setForm((f) => ({ ...f, kundennummer: e.target.value }))}
                hint="Hero-Kundennummer (Customer.nr) – erscheint auf Rechnungen"
              />
              <TextInput
                label="Kundencode"
                type="number"
                min={1}
                value={form.kundencode}
                onChange={(e) => setForm((f) => ({ ...f, kundencode: e.target.value }))}
                hint="Interne Projektnummern-Gruppe – nicht die Kundennummer"
              />
            </>
          ) : (
            <TextInput
              label="Lieferantennummer"
              value={form.lieferantennummer}
              onChange={(e) => setForm((f) => ({ ...f, lieferantennummer: e.target.value }))}
              hint="Muss eindeutig sein – doppelte Lieferantennummern werden abgelehnt."
              className="sm:col-span-2"
            />
          )}
          <PartnerNameFields
            typ={typ}
            form={form}
            onChange={(name) => setForm((current) => ({ ...current, ...name }))}
          />
          <TextInput
            label="1. Adresszeile"
            value={form.adresszeile1}
            onChange={(e) => setForm((f) => ({ ...f, adresszeile1: e.target.value }))}
            hint="Namenszusatz oder zusätzliche Adresszeile wie in Hero"
          />
          <TextInput
            label="2. Adresszeile"
            value={form.adresszeile2}
            onChange={(e) => setForm((f) => ({ ...f, adresszeile2: e.target.value }))}
          />
          <TextInput
            label="E-Mail"
            type="email"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          />
          <TextInput
            label="Telefon"
            value={form.telefon}
            onChange={(e) => setForm((f) => ({ ...f, telefon: e.target.value }))}
          />
          <TextInput
            label="Straße"
            value={form.strasse}
            onChange={(e) => setForm((f) => ({ ...f, strasse: e.target.value }))}
            className="sm:col-span-2"
          />
          <TextInput
            label="PLZ"
            value={form.plz}
            onChange={(e) => setForm((f) => ({ ...f, plz: e.target.value }))}
          />
          <TextInput
            label="Ort"
            value={form.ort}
            onChange={(e) => setForm((f) => ({ ...f, ort: e.target.value }))}
          />
          <TextareaInput
            label="Notizen"
            value={form.notizen}
            onChange={(e) => setForm((f) => ({ ...f, notizen: e.target.value }))}
            rows={5}
            className="sm:col-span-2"
            hint="Interne Hinweise – bei Hero-Sync aus den Hero-Notizen übernommen."
          />
        </div>
      </Dialog>
    </AppShell>
  );
}
